/**
 * orgStructureService.js — REST client for the Org Structure API.
 *
 * Backend: services/tenant-api/routers/org_structure.py (mounted at /api →
 * reached here under /api/tenant/api/org-structure/*). Drives the
 * client-defined N-tier team hierarchy: tier registry, per-tier unit tables
 * (team_tier_N), member assignment, the roll-up resolver, and Excel
 * import / template export.
 */

import { coalesceInFlight, reuseRecent, trackedFetch } from './inflight';

const TIER_LEVEL = 'tier_level';

// Every request in this module goes through `trackedFetch`, which bumps the
// in-flight epoch on any non-GET. That is what makes a read issued AFTER a
// write safe to coalesce: it can never be handed the answer to a read that
// started before the write. Shadowing `fetch` here means a new write site
// cannot forget (see inflight.js for the alert-rule bug this comes from).
const fetch = trackedFetch;

const MANAGER_BASE = '/api/tenant';
const url = (suffix) => `${MANAGER_BASE}/api/org-structure${suffix}`;

function authHeaders() {
  // Omit Authorization entirely when there's no token — sending "Bearer null"
  // yields a confusing 500/odd 401 instead of a clean unauthenticated 401.
  const token = localStorage.getItem('authToken');
  return { ...(token ? { Authorization: `Bearer ${token}` } : {}), 'Content-Type': 'application/json' };
}
function authHeadersNoJSON() {
  const token = localStorage.getItem('authToken');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

/** GET + `handle`, sharing an identical request that is already in flight. */
async function getJson(suffix) {
  return coalesceInFlight(`GET ${suffix}`, async () => handle(
    await fetch(url(suffix), { headers: authHeaders() })));
}

async function handle(resp) {
  if (!resp.ok) {
    const data = await resp.json().catch(() => ({}));
    // Some refusals carry a structured `detail` (which members blocked a sync,
    // which pool was exceeded). Stringifying that into the message would show
    // the user the literal "[object Object]", so the object rides along on the
    // error and the message falls back to its own `message` field.
    const flat = typeof data.detail === 'string' ? data.detail : null;
    const err = new Error(flat || (data.detail && data.detail.message)
      || `Request failed (${resp.status})`);
    err.status = resp.status;
    err.detail = data.detail;
    throw err;
  }
  return resp.json();
}

// ── tiers ────────────────────────────────────────────────────────────
export async function listTiers() {
  return getJson('/tiers');
}
export async function createTier(body) {
  return handle(await fetch(url('/tiers'), {
    method: 'POST', headers: authHeaders(), body: JSON.stringify(body),
  }));
}
export async function updateTier(level, body) {
  return handle(await fetch(url(`/tiers/${level}`), {
    method: 'PUT', headers: authHeaders(), body: JSON.stringify(body),
  }));
}
export async function deleteTier(level, force = false) {
  return handle(await fetch(url(`/tiers/${level}?force=${force ? 'true' : 'false'}`), {
    method: 'DELETE', headers: authHeaders(),
  }));
}

// ── units ────────────────────────────────────────────────────────────
export async function getTree() {
  return handle(await fetch(url('/tree'), { headers: authHeaders() }));
}
export async function listUnits(tier) {
  return handle(await fetch(url(`/units?tier=${tier}`), { headers: authHeaders() }));
}
export async function createUnit(body) {
  return handle(await fetch(url('/units'), {
    method: 'POST', headers: authHeaders(), body: JSON.stringify(body),
  }));
}
export async function updateUnit(level, unitId, body) {
  return handle(await fetch(url(`/units/${level}/${encodeURIComponent(unitId)}`), {
    method: 'PUT', headers: authHeaders(), body: JSON.stringify(body),
  }));
}
export async function deleteUnit(level, unitId, force = false) {
  return handle(await fetch(
    url(`/units/${level}/${encodeURIComponent(unitId)}?force=${force ? 'true' : 'false'}`),
    { method: 'DELETE', headers: authHeaders() },
  ));
}
export async function unitEmails(level, unitId) {
  return handle(await fetch(
    url(`/units/${level}/${encodeURIComponent(unitId)}/emails`), { headers: authHeaders() },
  ));
}

// ── members ──────────────────────────────────────────────────────────
export async function listMembers(tierLevel, unitId) {
  return handle(await fetch(
    url(`/members?tierLevel=${tierLevel}&unitId=${encodeURIComponent(unitId)}`),
    { headers: authHeaders() },
  ));
}
export async function addMember(body) {
  return handle(await fetch(url('/members'), {
    method: 'POST', headers: authHeaders(), body: JSON.stringify(body),
  }));
}
export async function updateMember(memberId, body) {
  return handle(await fetch(url(`/members/${encodeURIComponent(memberId)}`), {
    method: 'PATCH', headers: authHeaders(), body: JSON.stringify(body),
  }));
}
export async function removeMember(memberId) {
  return handle(await fetch(url(`/members/${encodeURIComponent(memberId)}`), {
    method: 'DELETE', headers: authHeaders(),
  }));
}
// Find members (not just units) by email/name — the tree's own search only
// matches unit name / lead / external_ref. Each hit carries its resolved
// { tierLevel, unitId } so the caller can jump straight to that unit.
export async function searchMembers(q) {
  return handle(await fetch(url(`/members/search?q=${encodeURIComponent(q)}`), { headers: authHeaders() }));
}

// ── project binding (Jira/Rally projects as leaf "Project" units) ──────────
// Discover bindable projects from the warehouse, preview their auto-detected
// members, and bind selected projects as leaf-tier units (pulling members).
export async function discoveredProjects(source = 'jira', q = '', activeDays = 90) {
  const params = new URLSearchParams({ source, active_days: String(activeDays) });
  if (q) {params.set('q', q);}
  return handle(await fetch(url(`/discovered-projects?${params.toString()}`), { headers: authHeaders() }));
}
export async function bindProjects(body) {
  return handle(await fetch(url('/bind-projects'), {
    method: 'POST', headers: authHeaders(), body: JSON.stringify(body),
  }));
}

// ── import / template ─────────────────────────────────────────────────
// Descriptive, source-aware download names so files are recognisable on disk:
//   blank    → org-structure-blank-template.xlsx
//   current  → org-structure-current-2026-06-11.xlsx
//   projects → jira-projects-for-org-import-2026-06-11.xlsx (or rally-…)
function templateFilename(mode, source) {
  const stamp = new Date().toISOString().slice(0, 10);
  const src = source === 'rally' ? 'rally' : 'jira';
  if (mode === 'projects') {return `${src}-projects-for-org-import-${stamp}.xlsx`;}
  if (mode === 'current') {return `org-structure-current-${stamp}.xlsx`;}
  return 'org-structure-blank-template.xlsx';
}
export async function downloadTemplate(mode = 'blank', source = 'jira') {
  const m = ['current', 'projects'].includes(mode) ? mode : 'blank';
  const qs = m === 'projects' ? `mode=${m}&source=${encodeURIComponent(source)}` : `mode=${m}`;
  const resp = await fetch(url(`/template?${qs}`), { headers: authHeadersNoJSON() });
  if (!resp.ok) {
    let msg = `Template download failed (${resp.status})`;
    try { msg = (await resp.json()).detail || msg; } catch { /* ignore */ }
    throw new Error(msg);
  }
  const blob = await resp.blob();
  const a = document.createElement('a');
  const href = URL.createObjectURL(blob);
  a.href = href;
  a.download = templateFilename(m, source);
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(href), OBJECT_URL_REVOKE_DELAY_MS);
}

// Give the browser time to start the download before the blob URL is released.
const OBJECT_URL_REVOKE_DELAY_MS = 1500;

/** Upload one or more files. dryRun=true returns a preview report without writing. */
export async function importStructure(files, dryRun = true) {
  const fd = new FormData();
  (Array.isArray(files) ? files : [files]).forEach((f) => fd.append('files', f));
  fd.append('dry_run', dryRun ? 'true' : 'false');
  return handle(await fetch(url('/import'), {
    method: 'POST', headers: authHeadersNoJSON(), body: fd,
  }));
}

// ── viewer org-position scope (permissions) ──────────────────────────
// The signed-in user's visible/editable units, for menu + page gating.
// Backend: /api/discovery/me/org-scope. Best-effort; callers degrade to a
// permissive default if it can't be fetched.
export async function getOrgScope() {
  // AuthContext applies auth more than once per page load (mount, token
  // refresh, storage events) and each pass fetched this again — measured 4x
  // at ~8s each on /discovery/org, the largest remaining duplicate. It lives
  // under /api/discovery rather than the org-structure prefix, so it needs the
  // coalescer directly instead of `getJson`.
  return coalesceInFlight('GET /api/discovery/me/org-scope', async () => handle(
    await fetch(`${MANAGER_BASE}/api/discovery/me/org-scope`, { headers: authHeaders() }),
  ));
}

// Org-unit hierarchy + the people in it, scoped to the caller — feeds the
// Individual dashboard's hierarchical person picker. { units, people }.
export async function getOrgPeopleTree() {
  return handle(await fetch(
    `${MANAGER_BASE}/api/discovery/org-people-tree`, { headers: authHeaders() },
  ));
}

// ── tier dashboard rollup (Discovery endpoint) ───────────────────────
// `days` is the trailing window; pass `{ start, end }` (ISO dates) to bill an
// explicit window instead (used by the Budget tab's prior-period spend column).
export async function getUnitSummary(tierLevel, unitId, days = 30, opts = {}) {
  const { start, end } = opts;
  const params = new URLSearchParams();
  params.set(TIER_LEVEL, String(tierLevel));
  params.set('unit_id', unitId);
  params.set('days', String(days));
  if (start && end) { params.set('start', start); params.set('end', end); }
  const path = `/api/discovery/unit-summary?${params.toString()}`;
  // The Budget tab's heaviest read: one per child unit, over the current AND
  // the previous period. A re-render re-fires the whole fan-out, and each call
  // takes seconds, so the identical query was measured in flight twice — eight
  // requests doing four requests' work, with the next tab queued behind them.
  // (The server single-flights this too; this stops the request being made at
  // all.) It lives under /api/discovery, not the org-structure prefix, so it
  // needs the coalescer directly rather than `getJson`.
  return coalesceInFlight(`GET ${path}`, async () => handle(
    await fetch(`${MANAGER_BASE}${path}`, { headers: authHeaders() }),
  ));
}

// ── direct child units + their member emails (group-by-lower-tier) ────────
export async function getChildEmails(tierLevel, unitId) {
  const params = new URLSearchParams();
  params.set(TIER_LEVEL, String(tierLevel ?? 0));
  if (unitId) {params.set('unit_id', unitId);}
  return getJson(`/child-emails?${params.toString()}`);
}

// ── cascading budgets (allocations) ───────────────────────────────────
// A unit's RECEIVED period (and pool window) is parent-defined and comes back
// on the response; `allocatePeriodType` (finer-or-equal to received) + `allocPeriod`
// (the selected sub-period) drive how this unit splits its pool to children.
export async function getAllocationSummary(tierLevel, unitId, opts = {}) {
  const { allocatePeriodType, allocPeriod, horizon } = opts;
  // Four independent components ask for this (OrgBudgetPanel, CascadeBudgetPanel,
  // SpendVsBudgetCard, useBudgetData) and several mount together, so the same
  // query went out twice per page load — each copy taking a slot the page's
  // other calls were queued behind. Shared while in flight, never cached.
  const params = new URLSearchParams();
  if (tierLevel != null) {params.set(TIER_LEVEL, String(tierLevel));}
  if (unitId != null) {params.set('unit_id', unitId);}
  if (allocatePeriodType) {params.set('allocate_period_type', allocatePeriodType);}
  if (allocPeriod) {params.set('alloc_period', allocPeriod);}
  // 'current' (active period) | 'next' (one period later — the planning horizon).
  if (horizon) {params.set('horizon', horizon);}
  return getJson(`/allocations/summary?${params.toString()}`);
}
export async function saveAllocation(body) {
  return handle(await fetch(url('/allocations'), {
    method: 'POST', headers: authHeaders(), body: JSON.stringify(body),
  }));
}

// ── financial autonomy ────────────────────────────────────────────────
// Whether a subject (a unit, or one person) may raise its own budget without
// the unit above approving. Opens only the PERMISSION gate — the funder still
// has to have room, so autonomy never creates money.
// `children[].canEdit` is resolved server-side so the switch can render
// read-only instead of offering an action that will 403; `fixed` marks the top
// tier, which is autonomous by definition and cannot be switched off.
// `scope: 'all'` returns every granted row in one call — for the org tree,
// which holds the whole hierarchy at once and would otherwise need one request
// per unit. Absent from the list means not autonomous.
// How long a slow, rarely-changing read is reused (see inflight.js reuseRecent).
const RECENT_MS = 30_000;

export async function getBudgetAutonomy(tierLevel, unitId, opts = {}) {
  const params = new URLSearchParams();
  if (tierLevel != null) {params.set(TIER_LEVEL, String(tierLevel));}
  if (unitId != null) {params.set('unit_id', unitId);}
  if (opts.scope) {params.set('scope', opts.scope);}
  // Several cards on one page ask for the same unit's autonomy at once (the
  // pool card, Request budget, the table): one request answers them all.
  const path = `/budget-autonomy?${params.toString()}`;
  return reuseRecent(`GET ${path}`, RECENT_MS, async () => handle(await fetch(url(path), { headers: authHeaders() })));
}
export async function setBudgetAutonomy(body) {
  return handle(await fetch(url('/budget-autonomy'), {
    method: 'POST', headers: authHeaders(), body: JSON.stringify(body),
  }));
}

// ── budget requests (the bottom-up half of the cascade) ───────────────
// `amountUsd` is the TOTAL the subject wants to reach, not the increase: the
// current figure can move while a request is open, so a delta would land on a
// different number than the one that was approved.
export async function listBudgetRequests(opts = {}) {
  const { scope, period, status } = opts;
  const params = new URLSearchParams();
  if (scope) {params.set('scope', scope);}            // incoming | outgoing
  if (period) {params.set('period', period);}
  if (status) {params.set('status', status);}
  return getJson(`/budget-requests?${params.toString()}`);
}
export async function createBudgetRequest(body) {
  return handle(await fetch(url('/budget-requests'), {
    method: 'POST', headers: authHeaders(), body: JSON.stringify(body),
  }));
}
export async function decideBudgetRequest(id, body) {
  return handle(await fetch(url(`/budget-requests/${encodeURIComponent(id)}/decide`), {
    method: 'POST', headers: authHeaders(), body: JSON.stringify(body),
  }));
}
export async function withdrawBudgetRequest(id) {
  return handle(await fetch(url(`/budget-requests/${encodeURIComponent(id)}/withdraw`), {
    method: 'POST', headers: authHeaders(),
  }));
}
// Hand-off for the "cannot lower below what the child committed" block: the
// parent cannot edit the child's own allocations, so it asks the child to
// reduce. Deliberately a message, not an approval object.
export async function notifyReductionNeeded(body) {
  return handle(await fetch(url('/budget-requests/notify-reduction'), {
    method: 'POST', headers: authHeaders(), body: JSON.stringify(body),
  }));
}

// A single member's cascade budget — the sum of allocations their leaf unit(s)
// handed them over the current/next received window, plus a per-unit `sources`
// breakdown. This is the per-person mirror of the Tier Budget page's per-member
// allocation, so the Individual "My Budget" view reconciles with it (rather
// than the legacy team-budget pool sum). Returns { cap, sources, periodType,
// periodLabel, window } — degrades to a zero cap when org structure is absent.
export async function getMemberAllocation(email, opts = {}) {
  const { horizon } = opts;
  const params = new URLSearchParams();
  params.set('email', email);
  if (horizon) {params.set('horizon', horizon);}
  const path = `/allocations/for-member?${params.toString()}`;
  return reuseRecent(`GET ${path}`, RECENT_MS, async () => handle(await fetch(url(path), { headers: authHeaders() })));
}

// ── people directory (for the member + lead pickers) ──────────────────
// Primary source is the org structure's OWN people (silver__people); the
// connector-derived roster (silver__catalog) is merged in as a fallback so the
// picker is populated even when the catalog isn't present. Deduped by email,
// org-structure names winning.
export async function listDirectory() {
  const fromOrg = fetch(url('/people'), { headers: authHeaders() })
    .then((r) => (r.ok ? r.json() : { people: [] }))
    .then((d) => (d.people || [])
      .filter((p) => p.email)
      .map((p) => ({ email: String(p.email).toLowerCase(), name: p.name || p.email })))
    .catch(() => []);
  const fromRoster = fetch(`${MANAGER_BASE}/api/discovery/roster`, { headers: authHeaders() })
    .then((r) => (r.ok ? r.json() : { members: [] }))
    .then((d) => (d.members || [])
      .filter((m) => m.email)
      .map((m) => ({ email: String(m.email).toLowerCase(), name: m.name || m.email })))
    .catch(() => []);
  const [org, roster] = await Promise.all([fromOrg, fromRoster]);
  const byEmail = new Map();
  [...org, ...roster].forEach((p) => { if (p.email && !byEmail.has(p.email)) {byEmail.set(p.email, p);} });
  return Array.from(byEmail.values());
}

// ── vendor spend-ceiling credential status ─────────────────────────────────
// Admin-only. Three checks per provider (read / write / feature-enabled), each
// tri-state: true / false / null meaning NOT CHECKED — which is a different
// fact from "failed" and has a different fix. Never collapse them in the UI.
export async function getVendorQuotaStatus() {
  return handle(await fetch(url('/vendor-quota/status'), { headers: authHeaders() }));
}

// ── per-member vendor ceiling ──────────────────────────────────────────────
// `enabled` is TRI-STATE: true = enforce, false = monitor only, null = nobody
// has decided. The key is only sent when the caller means to change it, so a
// split-only edit cannot silently un-decide someone.
export async function setMemberCeiling({ toTierLevel, toUnitId, fromTierLevel, fromUnitId,
                                         period, periodType, enabled, splitGh, split }) {
  const body = { toTierLevel, toUnitId, fromTierLevel, fromUnitId, period, periodType };
  if (enabled !== undefined) {body.enabled = enabled;}
  // `split` is the three-way {github_copilot, claude_code, cursor} split and
  // wins over the older Copilot-only `splitGh`.
  if (split !== undefined) {body.split = split;}
  else if (splitGh !== undefined) {body.splitGh = splitGh;}
  return handle(await fetch(url('/vendor-quota/member'), {
    method: 'POST',
    headers: { ...authHeaders(), 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  }));
}

// A person's own trailing Copilot/Claude mix. Offered as a suggestion next to
// the split field and never applied on its own — a split that followed habits
// automatically would rewrite ceilings nobody asked to change.
export async function getSplitSuggestion(email, days = 90) {
  const params = new URLSearchParams({ email, days: String(days) });
  return handle(await fetch(url(`/vendor-quota/split-suggestion?${params.toString()}`),
    { headers: authHeaders() }));
}

// Three states per member of a unit, plus the reason when one needs review.
// Reads the daily SNAPSHOT, never the vendor API: 40 members would be 40 vendor
// requests per render, starving the write path of the rate limit it shares.
export async function getCeilingStates(tierLevel, unitId, { period, periodType } = {}) {
  const params = new URLSearchParams({ tier_level: String(tierLevel), unit_id: unitId });
  if (period) {params.set('period', period);}
  if (periodType) {params.set('period_type', periodType);}
  return handle(await fetch(url(`/vendor-quota/ceilings?${params.toString()}`),
    { headers: authHeaders() }));
}

// Org-wide ceiling coverage + the issues worth acting on. Reads the ledger and
// the daily snapshot only — never a vendor API, which at 400 people would be 400
// requests per page render.
export async function getCeilingCoverage() {
  // Two components ask for this on the same page load and it takes 6-8s on a
  // seeded org, so both copies were in flight at once.
  return getJson('/vendor-quota/coverage');
}

// ── vendor ceiling sync ──────────────────────────────────────────────────
// The preview and the run are computed by the same server code, so what the
// confirm step promises is what the run does.
export async function previewCeilingSync(tierLevel, unitId, { period, periodType } = {}) {
  const params = new URLSearchParams({ tier_level: String(tierLevel), unit_id: unitId });
  if (period) {params.set('period', period);}
  if (periodType) {params.set('period_type', periodType);}
  return handle(await fetch(url(`/vendor-quota/sync/preview?${params.toString()}`),
    { headers: authHeaders() }));
}

// `dryRun` defaults to true on the SERVER as well — a caller that forgets the
// flag gets a preview, never a write.
export async function startCeilingSync({ tierLevel, unitId, period, periodType, dryRun }) {
  return handle(await fetch(url('/vendor-quota/sync'), {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ tierLevel, unitId, period, periodType, dryRun }),
  }));
}

export async function getCeilingSyncRun(runId) {
  return handle(await fetch(url(`/vendor-quota/sync/${encodeURIComponent(runId)}`),
    { headers: authHeaders() }));
}

// The unit's most recent run. This is what makes "closing the tab is safe" true
// rather than a claim: the bar reopens onto whatever is still running.
export async function getLatestCeilingSync(tierLevel, unitId) {
  const params = new URLSearchParams({ tier_level: String(tierLevel), unit_id: unitId });
  return handle(await fetch(url(`/vendor-quota/sync?${params.toString()}`),
    { headers: authHeaders() }));
}

// The two ways out of a conflict. Named for what they do, not for who wins:
// `rewrite` drops AFM's claim so the next sync writes ours over theirs;
// `keep` leaves their number alone and stops managing that person.
export async function resolveCeilingConflict(body) {
  return handle(await fetch(url('/vendor-quota/conflict'), {
    method: 'POST', headers: authHeaders(), body: JSON.stringify(body),
  }));
}

// The FULL check for one vendor, including whether its credential can set a
// quota. A POST, not a GET, because it makes one change that undoes itself —
// neither vendor can be asked what scopes a token has, so the only way to know
// is to try. It happens when a person presses the button, never on page load.
export async function checkVendorQuotaStatus(provider, probeEmail) {
  return handle(await fetch(url('/vendor-quota/status/check'), {
    method: 'POST', headers: authHeaders(),
    body: JSON.stringify({ ...(provider ? { provider } : {}), probeEmail }),
  }));
}

// Who may sync this unit. An admin grants it, the unit's lead performs it —
// so the lead needs to be able to SEE that the grant is missing, or the feature
// merely appears not to exist for them.
export async function getSyncCapability(tierLevel, unitId) {
  const params = new URLSearchParams({ tier_level: String(tierLevel), unit_id: unitId });
  return handle(await fetch(url(`/vendor-quota/capability?${params.toString()}`),
    { headers: authHeaders() }));
}

export async function setSyncCapability({ tierLevel, unitId, enabled }) {
  return handle(await fetch(url('/vendor-quota/capability'), {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ tierLevel, unitId, enabled }),
  }));
}

export async function cancelCeilingSync(runId) {
  return handle(await fetch(url(`/vendor-quota/sync/${encodeURIComponent(runId)}/cancel`),
    { method: 'POST', headers: authHeaders() }));
}
